import React from 'react';
import PropTypes from 'prop-types';
import '../../pages/TenderOpening/components/TenderOpening.css';
import './StatusBadge.css';

export const STATUS_TONES = [
  'success',
  'warning',
  'danger',
  'info',
  'primary',
  'muted',
];

// Each tone has its own icon shape, so a status is never told apart by colour
// alone (colour-blind users, greyscale print, low-contrast screens).
export const STATUS_TONE_ICONS = {
  success: 'ri-checkbox-circle-fill',
  warning: 'ri-time-fill',
  danger: 'ri-close-circle-fill',
  info: 'ri-information-fill',
  primary: 'ri-play-circle-fill',
  muted: 'ri-checkbox-blank-circle-line',
};

// Case-insensitive label -> tone. Anything unknown falls back to `muted`,
// so a new backend wording never renders as an unstyled string.
const LABEL_TONES = {
  success: [
    'done',
    'completed',
    'complete',
    'confirmed',
    'approved',
    'submitted',
    'active',
    'online',
    'published',
    'awarded',
    'closed',
    'yes',
    'ready',
    'accepted',
    'verified',
    'price accepted',
    'bidding open',
    'read',
  ],
  warning: [
    'draft',
    'drafted',
    'pending',
    'waiting',
    'in progress',
    'on progress',
    'inprogress',
    'under evaluation',
    'under review',
    'processing',
    'bypass',
    'past deadline',
    'waiting for buyer',
    'waiting response from registered supplier',
    'waiting response from panitia tender',
  ],
  danger: [
    'rejected',
    'cancelled',
    'canceled',
    'failed',
    'expired',
    'late submit',
    'inactive',
    'disable',
    'disabled',
    'locked',
    'blocked',
    'awaiting evaluation',
    'error',
    'bidding closed',
    'closing',
    'closing clarification',
  ],
  info: [
    'open',
    'new',
    'request',
    'requested',
    'default',
    'header',
    'ready for evaluation',
    'waiting for seller',
    'announced',
  ],
  primary: ['under negotiation', 'opening'],
  muted: [
    'no',
    'n/a',
    'none',
    'unpublished',
    'not started',
    'unknown',
    'unread',
    'saved evaluation',
    'closing evaluation',
    'locked evaluation',
    'evaluation locked',
  ],
};

export const getStatusTone = label => {
  const key = String(label ?? '')
    .trim()
    .toLowerCase();
  const tone = Object.keys(LABEL_TONES).find(t => LABEL_TONES[t].includes(key));
  return tone || 'muted';
};

// Bridge for legacy reactstrap colour names (`<Badge color='warning'>`) that
// still come out of per-module meta maps.
const COLOR_TONES = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  primary: 'primary',
  secondary: 'muted',
  light: 'muted',
  dark: 'muted',
  muted: 'muted',
};

export const colorToTone = color => COLOR_TONES[color] || 'muted';

/**
 * One status look everywhere: pill + tone icon + readable text.
 * Pass `tone` when the page already knows it, otherwise it is derived from
 * the label. `size='sm'` is for dense tables / inline hero meta.
 */
const StatusBadge = ({
  label,
  tone,
  icon,
  size,
  title,
  className = '',
  ...rest
}) => {
  if (label === null || label === undefined || label === '') {
    return <span className='text-muted'>-</span>;
  }
  const resolved = STATUS_TONES.includes(tone) ? tone : getStatusTone(label);
  const classes = [
    'to-status',
    `to-status--${resolved}`,
    'to-status--icon',
    size === 'sm' ? 'to-status--sm' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const text = typeof label === 'string' ? label : undefined;

  return (
    <span className={classes} title={title ?? text} {...rest}>
      <i
        className={`to-status__icon ${icon || STATUS_TONE_ICONS[resolved]}`}
        aria-hidden='true'
      />
      <span className='to-status__text'>{label}</span>
    </span>
  );
};

StatusBadge.propTypes = {
  label: PropTypes.node,
  tone: PropTypes.oneOf(STATUS_TONES),
  icon: PropTypes.string,
  size: PropTypes.oneOf(['sm']),
  title: PropTypes.string,
  className: PropTypes.string,
};

export default StatusBadge;
